// Unit conversion utilities for chemical amounts

// Define unit groups and their base conversions
export const UNIT_GROUPS = {
  volume: {
    gallon: { factor: 1, display: 'gal', full: 'gallon' },
    quart: { factor: 4, display: 'qt', full: 'quart' },
    pint: { factor: 8, display: 'pt', full: 'pint' },
    cup: { factor: 16, display: 'cups', full: 'cup' },
    fluid_ounce: { factor: 128, display: 'fl oz', full: 'fluid ounce' }
  },
  weight: {
    pound: { factor: 1, display: 'lbs', full: 'pound' },
    ounce: { factor: 16, display: 'oz', full: 'ounce' },
    kilogram: { factor: 0.453592, display: 'kg', full: 'kilogram' },
    gram: { factor: 453.592, display: 'g', full: 'gram' }
  },
  count: {
    tablet: { factor: 1, display: 'tablets', full: 'tablet' },
    piece: { factor: 1, display: 'pieces', full: 'piece' }
  }
}

// Get all units for a given unit type
export const getUnitsForType = (unitType) => {
  return UNIT_GROUPS[unitType] || {}
}

// Get the unit group that contains a specific unit
export const getUnitGroup = (unit) => {
  for (const [groupName, units] of Object.entries(UNIT_GROUPS)) {
    if (units[unit]) {
      return groupName
    }
  }
  return null
}

// Convert between units within the same group
export const convertUnit = (value, fromUnit, toUnit) => {
  const fromGroup = getUnitGroup(fromUnit)
  const toGroup = getUnitGroup(toUnit)
  
  // Can only convert within the same unit group
  if (!fromGroup || !toGroup || fromGroup !== toGroup) {
    return null
  }
  
  const units = UNIT_GROUPS[fromGroup]
  const fromFactor = units[fromUnit].factor
  const toFactor = units[toUnit].factor
  
  // Convert to base unit first, then to target unit
  const baseValue = value / fromFactor
  const convertedValue = baseValue * toFactor
  
  return Math.round(convertedValue * 100) / 100 // Round to 2 decimal places
}

// Get available units for conversion based on current unit
export const getAvailableUnits = (currentUnit) => {
  const group = getUnitGroup(currentUnit)
  if (!group) return []
  
  return Object.entries(UNIT_GROUPS[group]).map(([key, value]) => ({
    value: key,
    label: value.display,
    fullName: value.full
  }))
}

// Format amount with unit display
export const formatAmountWithUnit = (amount, unit) => {
  const group = getUnitGroup(unit)
  if (!group) return `${amount} ${unit}`
  
  const unitInfo = UNIT_GROUPS[group][unit]
  if (!unitInfo) return `${amount} ${unit}`
  
  return `${amount} ${unitInfo.display}`
}

// Get the most appropriate unit for a given amount (to avoid tiny decimals or huge numbers)
export const suggestBestUnit = (amount, currentUnit) => {
  if (amount <= 0) return currentUnit

  const availableUnits = getAvailableUnits(currentUnit)
  if (availableUnits.length <= 1) return currentUnit

  let bestUnit = currentUnit
  let bestScore = Math.abs(Math.log10(amount)) // Prefer amounts closer to 1
  
  availableUnits.forEach(unit => {
    const convertedAmount = convertUnit(amount, currentUnit, unit.value)
    if (convertedAmount !== null) {
      const score = Math.abs(Math.log10(convertedAmount))
      if (score < bestScore) {
        bestScore = score
        bestUnit = unit.value
      }
    }
  })
  
  return bestUnit
}